import * as Icons from '@components/Icons';

export interface NavSection {
  section: string;
}

export interface NavItem {
  id: string;
  path: string;
  label: string;
  Icon: React.ComponentType<{ size?: number }>;
  superOnly?: boolean;
  mgrAccess?: boolean;
  /** Also visible to superadmin (combine with mgrAccess for "both roles"). */
  superAccess?: boolean;
  tag?: string;
}

export type NavEntry = NavSection | NavItem;

export function isSection(entry: NavEntry): entry is NavSection {
  return 'section' in entry;
}

// mgrAccess = center_admin. superOnly = superadmin only. superAccess = also
// superadmin (with mgrAccess → both center_admin and superadmin). Neither = all roles.
//
// Every operational page now carries `superAccess: true` so superadmin can
// enter and inspect any centre via the global CenterSwitcher in the sidebar.
export const NAV: NavEntry[] = [
  { section: 'resumen' },
  { id: 'inicio', path: '/admin/inicio', label: 'Inicio', Icon: Icons.Pulse, mgrAccess: true, superAccess: true, tag: 'NEW' },

  { section: 'operación' },
  { id: 'bookings', path: '/admin/bookings', label: 'Reservas', Icon: Icons.Book, mgrAccess: true, superAccess: true },
  { id: 'calendar', path: '/admin/calendar', label: 'Calendario', Icon: Icons.Calendar, mgrAccess: true, superAccess: true },
  { id: 'customers', path: '/admin/customers', label: 'Clientes', Icon: Icons.Users, mgrAccess: true, superAccess: true },

  { section: 'catálogo' },
  { id: 'staff', path: '/admin/staff', label: 'Staff', Icon: Icons.Building, mgrAccess: true, superAccess: true },
  { id: 'horarios', path: '/admin/horarios', label: 'Horarios', Icon: Icons.Clock, superOnly: true, tag: 'NEW' },
  // Servicios = catálogo GLOBAL (post-0028). Sólo superadmin gestiona el catálogo
  // y las compatibilidades staff/salas.
  { id: 'services', path: '/admin/services', label: 'Servicios', Icon: Icons.List, superOnly: true },
  // Centros: superadmin ve todos; center_admin ve solo los suyos (filtrado en
  // backend por `user.adminCenterIds`). Salas se gestionan desde el tab
  // "Salas" del CenterDrawer (la página /admin/rooms se eliminó).
  { id: 'centers', path: '/admin/centers', label: 'Centros', Icon: Icons.Storefront, mgrAccess: true, superAccess: true },

  { section: 'analítica' },
  { id: 'informes', path: '/admin/informes', label: 'Informes', Icon: Icons.Chart, superOnly: true, tag: 'NEW' },
  { id: 'historial', path: '/admin/historial', label: 'Historial', Icon: Icons.Clipboard },

  { section: 'plataforma' },
  { id: 'guia', path: '/admin/guia', label: 'Guía', Icon: Icons.Book, tag: 'NEW' },
  { id: 'sistema', path: '/admin/sistema', label: 'Sistema', Icon: Icons.Heart, superOnly: true, tag: 'NEW' },
  // Configuración sin flags = visible para los 3 roles (incluye staff, que
  // solo verá "Mi perfil" + "Mi cuenta" dentro de la página).
  { id: 'settings', path: '/admin/settings', label: 'Configuración', Icon: Icons.Gear },
];

// Empty by design: superadmin used to be redirected from these paths, but now
// they pick the centre context via the CenterSwitcher. Constant kept to keep
// `AdminLayout`'s shape predictable in case the inverse migration is needed.
export const MGR_ONLY_PATHS: readonly string[] = [];

export const SUPER_ONLY_PATHS: readonly string[] = [
  '/admin/horarios',
  '/admin/informes',
  '/admin/services',
  '/admin/sistema',
];

// Role IDs duplicated here to keep this frontend module free of backend types.
// The canonical list lives in src/domain/staff/role.ts on the worker side.
export const FRONTEND_ROLES = ['superadmin', 'center_admin', 'staff'] as const;
export type FrontendRole = (typeof FRONTEND_ROLES)[number];

/**
 * Translate every NavItem into the explicit list of roles that can see it.
 * Mirrors the in-component filter (AdminLayout `visibleNav`):
 *  - `superOnly`   → only superadmin
 *  - flags combo   → union of `mgrAccess`(center_admin) + `superAccess`(superadmin)
 *  - no flag       → all three roles
 *
 * Used by the "Control de acceso" panel to render the rol × sección admin table.
 */
export function getNavMatrix(): { id: string; label: string; path: string; allowedRoles: readonly FrontendRole[] }[] {
  const rows: { id: string; label: string; path: string; allowedRoles: readonly FrontendRole[] }[] = [];
  for (const entry of NAV) {
    if (isSection(entry)) continue;
    const allowed: FrontendRole[] = [];
    if (entry.superOnly) {
      allowed.push('superadmin');
    } else if (entry.mgrAccess === true || entry.superAccess === true) {
      if (entry.superAccess === true) allowed.push('superadmin');
      if (entry.mgrAccess === true) allowed.push('center_admin');
    } else {
      // No flag = visible to all authenticated roles.
      allowed.push('superadmin', 'center_admin', 'staff');
    }
    rows.push({ id: entry.id, label: entry.label, path: entry.path, allowedRoles: allowed });
  }
  return rows;
}
